import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { AgentRole } from '@/types/agentTypes';

interface AgentAvatarSelectorProps {
  role: AgentRole;
  selectedAvatar: string;
  onSelect: (avatar: string) => void;
  className?: string;
}

/**
 * Component for selecting an avatar for an agent
 */
const AgentAvatarSelector: React.FC<AgentAvatarSelectorProps> = ({
  role,
  selectedAvatar,
  onSelect,
  className = ''
}) => {
  const [avatars, setAvatars] = useState<string[]>(role.avatarOptions || []);
  
  // Update avatars when role changes
  useEffect(() => {
    setAvatars(role.avatarOptions || []);
    
    // If the current selected avatar is not in the new role's options,
    // select the first avatar from the new role
    if (role.avatarOptions && !role.avatarOptions.includes(selectedAvatar) && role.avatarOptions.length > 0) {
      onSelect(role.avatarOptions[0]);
    }
  }, [role, selectedAvatar, onSelect]);
  
  if (!avatars || avatars.length === 0) {
    return (
      <div className={`${className} p-4 text-center text-gray-500`}>
        No avatars available for this role
      </div>
    );
  }
  
  return (
    <div className={className}>
      <Label className="mb-2 block">Select Avatar</Label>
      <div className="grid grid-cols-4 gap-4">
        {avatars.map((avatar, index) => (
          <div
            key={index}
            className={`
              cursor-pointer rounded-md p-2 border-2 transition-all
              ${selectedAvatar === avatar ? 'border-violet-500 bg-violet-50' : 'border-transparent hover:bg-gray-50'}
            `}
            onClick={() => onSelect(avatar)}
          >
            <div className="w-16 h-16 mx-auto bg-gray-200 rounded-full overflow-hidden">
              {avatar ? (
                <img 
                  src={avatar} 
                  alt={`Avatar ${index + 1}`} 
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    // If image fails to load, show a placeholder
                    (e.target as HTMLImageElement).src = '/images/avatars/placeholder.png';
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  No Image
                </div>
              )}
            </div>
            <div className="text-xs text-center mt-1 text-gray-600">
              Avatar {index + 1}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/**
 * Component for displaying an agent's avatar
 */
export const AgentAvatar: React.FC<{
  avatarUrl: string;
  name: string;
  size?: 'sm' | 'md' | 'lg';
  status?: 'online' | 'offline' | 'busy';
  className?: string;
}> = ({
  avatarUrl,
  name,
  size = 'md',
  status,
  className = ''
}) => {
  const getSizeClasses = () => {
    switch (size) {
      case 'sm': return 'w-8 h-8';
      case 'md': return 'w-12 h-12';
      case 'lg': return 'w-16 h-16';
      default: return 'w-12 h-12';
    }
  };
  
  const getStatusClasses = () => {
    if (!status) return '';
    
    const baseClasses = 'absolute bottom-0 right-0 rounded-full border-2 border-white';
    const sizeClasses = size === 'sm' ? 'w-2 h-2' : size === 'md' ? 'w-3 h-3' : 'w-4 h-4';
    
    const colorClasses = {
      online: 'bg-green-500',
      offline: 'bg-gray-400',
      busy: 'bg-red-500'
    }[status];
    
    return `${baseClasses} ${sizeClasses} ${colorClasses}`;
  };
  
  return (
    <div className={`relative inline-block ${className}`}>
      <div className={`${getSizeClasses()} rounded-full overflow-hidden bg-gray-200`}>
        {avatarUrl ? (
          <img 
            src={avatarUrl} 
            alt={name} 
            className="w-full h-full object-cover"
            onError={(e) => {
              // If image fails to load, show initials
              (e.target as HTMLImageElement).style.display = 'none';
              (e.target as HTMLImageElement).parentElement!.classList.add('flex', 'items-center', 'justify-center', 'bg-violet-100', 'text-violet-800');
              (e.target as HTMLImageElement).parentElement!.innerHTML = `<span>${name.charAt(0).toUpperCase()}</span>`;
            }}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-violet-100 text-violet-800">
            {name.charAt(0).toUpperCase()}
          </div>
        )}
      </div>
      {status && <div className={getStatusClasses()} />}
    </div>
  );
};

export default AgentAvatarSelector;